import React, { useEffect, useState } from 'react';
import { auth } from '../../firebase';
import { 
  UsersIcon, 
  CalendarDaysIcon, 
  CurrencyDollarIcon, 
  MagnifyingGlassIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

const DashboardContent = ({ stats, dashboardData, loading }) => {
  const { t } = useLanguage();
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedVinCheck, setSelectedVinCheck] = useState(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showVinCheckModal, setShowVinCheckModal] = useState(false);

  // Calculate revenue
  const calculateRevenue = () => {
    if (!dashboardData) return 0;
    const paidVinChecks = dashboardData.vinChecks?.filter(check => check.paid)?.length || 0;
    const vinCheckPrice = 20; // 20 RON per VIN check
    const stripeFee = 0.029; // 2.9% Stripe fee
    const grossRevenue = paidVinChecks * vinCheckPrice;
    const netRevenue = grossRevenue * (1 - stripeFee);
    return { gross: grossRevenue, net: netRevenue.toFixed(2), count: paidVinChecks };
  };

  const revenue = calculateRevenue();

  // Helper to format local date/time
  const formatLocalDateTime = (utcString) => {
    if (!utcString) return 'N/A';
    const date = new Date(utcString);
    return date.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false
    });
  };

  // Modal handlers
  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setShowAppointmentModal(true);
  };

  const handleVinCheckClick = (vinCheck) => {
    setSelectedVinCheck(vinCheck);
    setShowVinCheckModal(true);
  };

  const closeAppointmentModal = () => {
    setShowAppointmentModal(false);
    setSelectedAppointment(null);
  };

  const closeVinCheckModal = () => {
    setShowVinCheckModal(false);
    setSelectedVinCheck(null);
  };

  // Get recent appointments and vin checks separately
  const getRecentAppointments = () => {
    if (!dashboardData) return [];
    // Sort by appointmentStart descending
    const sorted = [...(dashboardData.appointments || [])].sort((a, b) => {
      const aDate = a.appointmentData?.appointmentStart ? new Date(a.appointmentData.appointmentStart) : new Date(0);
      const bDate = b.appointmentData?.appointmentStart ? new Date(b.appointmentData.appointmentStart) : new Date(0);
      return bDate - aDate;
    });
    return sorted.slice(0, 3).map(apt => ({
      action: `New appointment: ${apt.shopName || 'Service'}`,
      time: apt.appointmentData?.appointmentStart ? formatLocalDateTime(apt.appointmentData.appointmentStart) : '',
      type: 'appointment',
      color: 'from-blue-500 to-blue-600',
      shopName: apt.shopName,
      car: apt.appointmentData?.carMake && apt.appointmentData?.carType ? `${apt.appointmentData.carMake} ${apt.appointmentData.carType}` : '',
      registrationNumber: apt.appointmentData?.registrationNumber,
      appointmentStart: apt.appointmentData?.appointmentStart,
      specialization: apt.appointmentData?.specialization,
      customer: apt.appointmentData?.name,
      confirmed: apt.confirmed,
      originalData: apt // Store original appointment data
    }));
  };
  const getRecentVinChecks = () => {
    if (!dashboardData) return [];
    // No sort, just take the last 3 in original order, latest on top
    return (dashboardData.vinChecks || [])
      .slice(-3)
      .reverse()
      .map(check => ({
        action: `VIN Check: ${check.vin || 'Unknown VIN'}`,
        time: check.createdAt ? formatLocalDateTime(check.createdAt) : '',
        type: 'vin',
        color: check.paid ? 'from-green-500 to-green-600' : 'from-orange-500 to-orange-600',
        paid: check.paid,
        vehicleInfo: check.vehicleInfo,
        originalData: check // Store original VIN check data
      }));
  };

  const recentAppointments = getRecentAppointments();
  const recentVinChecks = getRecentVinChecks();

  useEffect(() => {
    const getAccessToken = async () => {
      if (auth.currentUser) {
        try {
          const token = await auth.currentUser.getIdToken();
          console.log('Firebase Access Token:', token);
        } catch (error) {
          console.error('Error getting access token:', error);
        }
      } else {
        console.log('No user is currently signed in');
      }
    };

    getAccessToken();
  }, []);


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <svg className="animate-spin h-12 w-12 text-purple-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="mt-4 text-lg text-gray-600">{t('loadingDashboard')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="mx-auto px-4 sm:px-6 md:px-8">
        {/* Stats - horizontally scrollable on mobile */}
        <div className="overflow-x-auto pb-2">
          <div className="flex gap-4 min-w-max sm:grid sm:grid-cols-2 lg:grid-cols-5 sm:min-w-0">
          {stats.map((stat, index) => {
            const icons = [CalendarDaysIcon, MagnifyingGlassIcon, UsersIcon, CurrencyDollarIcon, CheckCircleIcon];
            const IconComponent = icons[index] || UsersIcon;
            const gradients = [
              'from-blue-500 to-blue-600',
              'from-green-500 to-green-600', 
              'from-purple-500 to-purple-600',
              'from-orange-500 to-orange-600',
              'from-blue-400 to-green-400'
            ];
            return (
              <div key={stat.name} className="bg-white overflow-hidden shadow-lg rounded-xl border border-slate-200 transition-all duration-300 transform w-52 flex-shrink-0 sm:w-auto">
                <div className="p-4 sm:p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br ${gradients[index]} rounded-xl flex items-center justify-center shadow-lg`}>
                        <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                    </div>
                    <div className="ml-4 sm:ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-xs sm:text-sm font-medium text-slate-500 truncate">{stat.name}</dt>
                        <dd>
                          <div className="text-xl sm:text-2xl font-bold text-slate-900">{stat.value}</div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
                <div className="bg-slate-50 px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-100">
                  <div className="text-xs sm:text-sm flex items-center">
                    {stat.changeType === 'positive' ? (
                      <ArrowUpIcon className="w-4 h-4 text-green-500 mr-1" />
                    ) : stat.changeType === 'negative' ? (
                      <ArrowDownIcon className="w-4 h-4 text-red-500 mr-1" />
                    ) : (
                      <ClockIcon className="w-4 h-4 text-slate-500 mr-1" />
                    )}
                    <span className={`font-semibold ${
                      stat.changeType === 'positive' ? 'text-green-600' : 
                      stat.changeType === 'negative' ? 'text-red-600' : 'text-slate-600'
                    }`}>
                      {stat.change}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          {/* Service Distribution Stat */}
          <div className="bg-white overflow-hidden shadow-lg rounded-xl border border-slate-200 transition-all duration-300 transform w-52 flex-shrink-0 sm:w-auto">
            <div className="p-4 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-400 to-green-400 rounded-xl flex items-center justify-center shadow-lg">
                    <CheckCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                </div>
                <div className="ml-4 sm:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs sm:text-sm font-medium text-slate-500 truncate">Service Distribution</dt>
                    <dd>
                      <div className="text-xl sm:text-2xl font-bold text-slate-900">{dashboardData?.appointments?.length || 0} / {dashboardData?.vinChecks?.length || 0}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
            <div className="bg-slate-50 px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-100">
              <div className="text-xs sm:text-sm flex flex-col">
                <span className="text-blue-700 font-semibold">Appointments: {dashboardData?.appointments?.length || 0} ({dashboardData?.appointments?.filter(apt => apt.confirmed)?.length || 0} confirmed)</span>
                <span className="text-green-700 font-semibold">VIN Checks: {dashboardData?.vinChecks?.length || 0} ({dashboardData?.vinChecks?.filter(check => check.paid)?.length || 0} paid)</span>
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Revenue Section */}
        <div className="mt-8">
          <div className="bg-white shadow-lg rounded-xl p-4 sm:p-8 border border-slate-200 transition-all duration-300">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-slate-900">{t('revenueOverview')}</h3>
              <div className="flex items-center space-x-2">
                <span className="text-xs sm:text-sm text-slate-500 bg-green-100 px-2 sm:px-3 py-1 rounded-full">VIN Checks: 20 RON each</span>
              </div>
            </div>
            <div className="overflow-x-auto pb-2">
              <div className="flex gap-4 min-w-max md:grid md:grid-cols-3 md:min-w-0">
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 sm:p-6 border border-green-200 w-48 flex-shrink-0 md:w-auto">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h4 className="text-xs sm:text-sm font-medium text-green-800">{t('grossRevenue')}</h4>
                  <CurrencyDollarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-green-900">{revenue.gross} RON</p>
                <p className="text-xs sm:text-sm text-green-600 mt-1">{revenue.count} {t('paidVinChecks')}</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 sm:p-6 border border-blue-200 w-48 flex-shrink-0 md:w-auto">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h4 className="text-xs sm:text-sm font-medium text-blue-800">{t('netRevenue')}</h4>
                  <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-blue-900">{revenue.net} RON</p>
                <p className="text-xs sm:text-sm text-blue-600 mt-1">{t('afterStripeFees')}</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 sm:p-6 border border-purple-200 w-48 flex-shrink-0 md:w-auto">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h4 className="text-xs sm:text-sm font-medium text-purple-800">{t('conversionRate')}</h4>
                  <MagnifyingGlassIcon className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-purple-900">
                  {dashboardData?.vinChecks?.length > 0 ?
                    Math.round((revenue.count / dashboardData.vinChecks.length) * 100) : 0}%
                </p>
                <p className="text-xs sm:text-sm text-purple-600 mt-1">{t('vinChecksConverted')}</p>
              </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <div className="mt-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Recent Appointments */}
            <div className="bg-white shadow-lg rounded-xl p-8 border border-slate-200 transition-all duration-300">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-900">{t('recentAppointments')}</h3>
                <span className="text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">{t('live')}</span>
              </div>
              <div className="space-y-4">
                {recentAppointments.length > 0 ? (
                  recentAppointments.map((activity, index) => (
                    <div 
                      key={index} 
                      className="flex items-center space-x-4 p-3 rounded-lg hover:bg-slate-50 transition-colors duration-200 cursor-pointer"
                      onClick={() => handleAppointmentClick(activity)}
                    >
                      <div className="flex-shrink-0">
                        <div className={`w-10 h-10 bg-gradient-to-br ${activity.color} rounded-xl flex items-center justify-center shadow-md`}>
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{activity.action}</p>
                        {activity.car && (
                          <p className="text-xs text-slate-700 mt-1">{activity.car}{activity.registrationNumber ? ` • ${activity.registrationNumber}` : ''}</p>
                        )}
                        {activity.specialization && (
                          <p className="text-xs text-blue-700">{activity.specialization}</p>
                        )}
                        {typeof activity.confirmed === 'boolean' && (
                          <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full font-semibold ${activity.confirmed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {activity.confirmed ? t('confirmed') : t('pending')}
                          </span>
                        )}
                        {activity.time && (
                          <p className="text-xs text-slate-500">{activity.time}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-slate-300 rounded-full"></div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <ClockIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">{t('noRecentAppointments')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent VIN Checks */}
            <div className="bg-white shadow-lg rounded-xl p-8 border border-slate-200 transition-all duration-300">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-900">{t('recentVinChecks')}</h3>
                <span className="text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">{t('live')}</span>
              </div>
              <div className="space-y-4">
                {recentVinChecks.length > 0 ? (
                  recentVinChecks.map((activity, index) => (
                    <div 
                      key={index} 
                      className="flex items-center space-x-4 p-3 rounded-lg hover:bg-slate-50 transition-colors duration-200 cursor-pointer"
                      onClick={() => handleVinCheckClick(activity)}
                    >
                      <div className="flex-shrink-0">
                        <div className={`w-10 h-10 bg-gradient-to-br ${activity.color} rounded-xl flex items-center justify-center shadow-md`}>
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{activity.action}</p>
                        <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full font-semibold ${activity.paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {activity.paid ? t('paid') : t('unpaid')}
                        </span>
                        <span className={`inline-block mt-1 ml-2 px-2 py-0.5 text-xs rounded-full font-semibold ${activity.vehicleInfo ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                          {activity.vehicleInfo ? t('vehicleInfo') : t('noVehicleInfo')}
                        </span>
                        {activity.time && (
                          <p className="text-xs text-slate-500 mt-1">{activity.time}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-slate-300 rounded-full"></div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <ClockIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">{t('noRecentVinChecks')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Appointment Details Modal */}
        {showAppointmentModal && selectedAppointment && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">{t('appointmentDetails')}</h3>
                <button
                  onClick={closeAppointmentModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">{t('shopName')}</label>
                    <p className="text-sm text-gray-900">{selectedAppointment.shopName || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">{t('status')}</label>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full font-semibold ${selectedAppointment.confirmed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {selectedAppointment.confirmed ? t('confirmed') : t('pending')}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">{t('customerName')}</label>
                    <p className="text-sm text-gray-900">{selectedAppointment.originalData?.appointmentData?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">{t('phone')}</label>
                    <p className="text-sm text-gray-900">{selectedAppointment.originalData?.appointmentData?.phone || 'N/A'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">{t('email')}</label>
                    <p className="text-sm text-gray-900">{selectedAppointment.originalData?.appointmentData?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">{t('appointmentDate')}</label>
                    <p className="text-sm text-gray-900">{selectedAppointment.time}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">{t('carMakeModel')}</label>
                    <p className="text-sm text-gray-900">{selectedAppointment.car || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">{t('registrationNumber')}</label>
                    <p className="text-sm text-gray-900">{selectedAppointment.registrationNumber || 'N/A'}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500">{t('specialization')}</label>
                  <p className="text-sm text-gray-900">{selectedAppointment.specialization || 'N/A'}</p>
                </div>

                {selectedAppointment.originalData?.appointmentData?.problemDescription && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">{t('problemDescription')}</label>
                    <p className="text-sm text-gray-900">{selectedAppointment.originalData.appointmentData.problemDescription}</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={closeAppointmentModal}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  {t('close')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* VIN Check Details Modal */}
        {showVinCheckModal && selectedVinCheck && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">{t('vinCheckDetails')}</h3>
                <button
                  onClick={closeVinCheckModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">{t('vinNumber')}</label>
                    <p className="text-sm font-mono text-gray-900">{selectedVinCheck.originalData?.vin || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">{t('paymentStatus')}</label>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full font-semibold ${selectedVinCheck.paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {selectedVinCheck.paid ? t('paid') : t('unpaid')}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">{t('email')}</label>
                    <p className="text-sm text-gray-900">{selectedVinCheck.originalData?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">{t('phone')}</label>
                    <p className="text-sm text-gray-900">{selectedVinCheck.originalData?.phone || 'N/A'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">{t('vehicleInfoAvailable')}</label>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full font-semibold ${selectedVinCheck.vehicleInfo ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                      {selectedVinCheck.vehicleInfo ? t('yes') : t('no')}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">{t('createdAt')}</label>
                    <p className="text-sm text-gray-900">{selectedVinCheck.time}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500">{t('sessionId')}</label>
                  <p className="text-xs font-mono text-gray-500 break-all select-all">{selectedVinCheck.originalData?.sessionId || 'N/A'}</p>
                </div>

                {selectedVinCheck.originalData?.vehicleInfo && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">{t('vehicleInformation')}</label>
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                      <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                        {JSON.stringify(selectedVinCheck.originalData.vehicleInfo, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={closeVinCheckModal}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  {t('close')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardContent;
